import type { users } from '@techgitter/contracts';
import {
  resolveAvatarColor,
  SYSTEM_AVATAR_COLOR,
  isSystemUserRef,
  type AvatarColour,
} from '../styles/avatarPalette';

/**
 * Index for resolving a user's avatar colour by id OR by display name.
 *
 * Avatars appear keyed by different identifiers across the app — work-item log
 * entries carry an author id or name, technician cards group by name, the topbar
 * uses the current user. This index lets every site map back to the same user
 * record so the chosen colour (and, when unset, the deterministic fallback) is
 * identical for a given user everywhere.
 */
export interface UserColorIndex {
  byId: Map<string, users>;
  byName: Map<string, users>;
}

const norm = (s: string | null | undefined): string =>
  (s || '').trim().toLowerCase();

/** Build a lookup index from the users list (id, guid, and full name). */
export function buildUserColorIndex(userList: users[]): UserColorIndex {
  const byId = new Map<string, users>();
  const byName = new Map<string, users>();
  for (const u of userList) {
    if (u.id) byId.set(u.id, u);
    if (u.guid) byId.set(u.guid, u);
    const name = norm(u.fullname);
    if (name) byName.set(name, u);
  }
  return { byId, byName };
}

/**
 * Resolve the avatar colour for whoever is identified by `id` and/or `name`.
 *
 * Looks the user up in the index (id first, then name) and uses that record's
 * stored colour, seeding the deterministic fallback with the user's stable id so
 * an unassigned user still gets the same colour at every avatar site. Falls back
 * to the raw id/name as the seed when the user isn't in the index.
 */
export function resolveUserColor(
  index: UserColorIndex,
  ref: { id?: string | null; name?: string | null },
): AvatarColour {
  const { id, name } = ref;

  // The automated system actor always renders in the reserved dark grey,
  // regardless of any deterministic seed — keeps it neutral everywhere
  // (work-item log entries, technician-card group headers, the topbar).
  if (isSystemUserRef(ref)) return SYSTEM_AVATAR_COLOR;

  const user =
    (id ? index.byId.get(id) : undefined) ??
    (name ? index.byName.get(norm(name)) : undefined);

  if (user) {
    return resolveAvatarColor({
      storedColor: user.avatarColor,
      seed: user.id || user.guid || norm(user.fullname),
    });
  }
  return resolveAvatarColor({ storedColor: undefined, seed: id || name || '' });
}
